
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface OrderItem {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  quantidade_pedido: number;
  valor_liquido: number;
  valor_total: number;
  is_optional?: boolean;
  product_id?: number;
}

interface CompactSegmentCardProps {
  segmentName: string;
  items: OrderItem[];
  formatCurrency: ( value: number ) => string;
  icon: string;
  /** Cupom por itens: desconto por produto (id do produto → valor), abatido do total do segmento. */
  couponDiscountByProduct?: Record<number, number>;
}

const CompactSegmentCard = ( { segmentName, items, formatCurrency, icon, couponDiscountByProduct }: CompactSegmentCardProps ) => {
  // console.log( 'CompactSegmentCard ==> ', { segmentName, items } );

  // Calculate segment metrics (excluding scheduled items)
  const nonScheduledItems = items;
  // const nonScheduledItems = items.filter(
  //   ( item ) =>
  //     !(
  //       item.descricao_produto.includes( "Plataforma AZ" ) &&
  //       item.descricao_produto.includes( "2º Semestre" )
  //     )
  // );

  const getSegmentTotal = () => {
    const gross = nonScheduledItems.reduce( ( total, item ) => total + item.valor_total, 0 );
    if ( !couponDiscountByProduct ) return gross;
    // O desconto do cupom é por produto; cada produto abate uma vez, mesmo que apareça em mais de uma linha.
    const discountedProducts = new Set<number>();
    let discount = 0;
    nonScheduledItems.forEach( ( item ) => {
      const productId = item.product_id ?? item.id;
      if ( discountedProducts.has( productId ) ) return;
      discountedProducts.add( productId );
      discount += couponDiscountByProduct[ productId ] ?? 0;
    } );
    return Math.max( 0, Math.round( ( gross - discount ) * 100 ) / 100 );
  };

  const getStudentProductsCount = () => {
    // Count unique students by series (each series has multiple materials but same student count)
    // Group by series and take the max quantity_pedido per series (represents number of students)
    // Produto opcional tem quantidade própria e não representa o número de alunos da série
    const studentsBySeriesMap = new Map<string, number>();

    nonScheduledItems
      .filter( ( item ) => item.utilizacao?.toLowerCase() === "aluno" && !item.is_optional )
      .forEach( ( item ) => {
        const serie = item.serie || "sem_serie";
        const currentMax = studentsBySeriesMap.get( serie ) || 0;

        // Use the maximum quantity_pedido for each series (all materials in a series have same student count)
        if ( item.quantidade_pedido > currentMax ) {
          studentsBySeriesMap.set( serie, item.quantidade_pedido );
        }
      } );

    // Sum the maximum quantities across all series in this segment
    return Array.from( studentsBySeriesMap.values() ).reduce( ( total, qty ) => total + qty, 0 );
  };

  const segmentTotal = getSegmentTotal();
  const studentProducts = getStudentProductsCount();
  const hasProducts = nonScheduledItems.length > 0;

  return (
    <Card className="hover:shadow-md transition-all duration-200 hover:scale-[1.02] bg-gradient-to-br from-muted/20 to-muted/5">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <span className="text-xl">{icon}</span>
          <div className="flex flex-col">
            <span className="font-semibold text-sm">{segmentName}</span>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="space-y-2">
          {hasProducts ? (
            <>
              {studentProducts > 0 && (
                <div className="text-xs text-muted-foreground">
                  <div>👨‍🎓 {studentProducts} aluno{studentProducts !== 1 ? 's' : ''}</div>
                </div>
              )}

              <div className="pt-2 border-t border-muted">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Total:</span>
                  <span className="font-bold text-primary">{formatCurrency( segmentTotal )}</span>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center text-muted-foreground text-xs py-2">
              Nenhum item selecionado
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CompactSegmentCard;
